import moment from "moment";
import { formatDate } from "helpers/Helpers";
import type { IEmailCampaign } from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";

export type TEmailCampaignScheduleType = IEmailCampaign["scheduled_type"];
export type TEmailCampaignRecurringType = NonNullable<IEmailCampaign["scheduled_recurring_type"]>;

export type TEmailCampaignScheduleFields = {
  scheduled_type: TEmailCampaignScheduleType | null;
  scheduled_at?: string | null;
  scheduled_recurring_type?: TEmailCampaignRecurringType | null;
};

export type TEmailCampaignSchedulePayload = {
  id: number;
  scheduled_type: TEmailCampaignScheduleType;
  scheduled_recurring_type: TEmailCampaignRecurringType | null;
  scheduled_at: string | null;
};

export const EMAIL_CAMPAIGN_RECURRING_OPTIONS: Array<{
  id: TEmailCampaignRecurringType;
  label: string;
}> = [
  { id: "recurring_weekly", label: "Every 7 days" },
  { id: "recurring_monthly", label: "Every Month" },
  { id: "recurring_biannually", label: "Every 6 Months" },
  { id: "recurring_annually", label: "Every Year" },
];

export function emailCampaignScheduleDate(campaign: TEmailCampaignScheduleFields): string {
  if (campaign.scheduled_at == null) {
    return "";
  }

  return moment(campaign.scheduled_at).format("YYYY-MM-DD");
}

export function emailCampaignScheduleTime(campaign: TEmailCampaignScheduleFields): string {
  if (campaign.scheduled_at == null) {
    return "";
  }

  return moment(campaign.scheduled_at).format("HH:mm");
}

export function updateEmailCampaignScheduleType(
  campaign: TEmailCampaignScheduleFields,
  selectedType: TEmailCampaignScheduleType,
): TEmailCampaignScheduleFields {
  if (selectedType === "now") {
    return {
      scheduled_type: "now",
      scheduled_at: null,
      scheduled_recurring_type: null,
    };
  }

  if (selectedType === "later") {
    return {
      scheduled_type: "later",
      scheduled_at: campaign.scheduled_at ?? null,
      scheduled_recurring_type: null,
    };
  }

  return {
    scheduled_type: "recurring",
    scheduled_at: campaign.scheduled_at ?? null,
    scheduled_recurring_type: campaign.scheduled_recurring_type ?? null,
  };
}

export function updateEmailCampaignScheduleDate(
  campaign: TEmailCampaignScheduleFields,
  date: Date,
): TEmailCampaignScheduleFields {
  const dateStr = formatDate(date);
  const time = emailCampaignScheduleTime(campaign);
  const scheduledAt = time.length > 0 ? `${dateStr} ${time}` : `${dateStr} 12:00` ;

  return {
    ...campaign,
    scheduled_at: scheduledAt,
  };
}

export function updateEmailCampaignScheduleTime(
  campaign: TEmailCampaignScheduleFields,
  time: string | undefined,
): TEmailCampaignScheduleFields {
  const date = emailCampaignScheduleDate(campaign);
  const scheduledAt =
    date.length > 0 && time != null && time.length > 0 ? `${date} ${time}` : null;

  return {
    ...campaign,
    scheduled_at: scheduledAt,
  };
}

export function updateEmailCampaignScheduleRecurringType(
  campaign: TEmailCampaignScheduleFields,
  recurringType: TEmailCampaignRecurringType,
): TEmailCampaignScheduleFields {
  return {
    ...campaign,
    scheduled_recurring_type: recurringType,
  };
}

export function isEmailCampaignScheduleCompleteFromCampaign(
  campaign: TEmailCampaignScheduleFields,
): boolean {
  if (campaign.scheduled_type == null) {
    return false;
  }

  if (campaign.scheduled_type === "now") {
    return true;
  }

  if (campaign.scheduled_at == null) {
    return false;
  }

  if (campaign.scheduled_type === "later") {
    return true;
  }

  return campaign.scheduled_recurring_type != null;
}

export function buildEmailCampaignSchedulePayloadFromCampaign(
  campaignId: number,
  campaign: TEmailCampaignScheduleFields,
): TEmailCampaignSchedulePayload | null {
  if (!isEmailCampaignScheduleCompleteFromCampaign(campaign) || campaign.scheduled_type == null) {
    return null;
  }

  return {
    id: campaignId,
    scheduled_type: campaign.scheduled_type,
    scheduled_recurring_type: campaign.scheduled_type === "recurring"
      ? campaign.scheduled_recurring_type ?? null
      : null,
    scheduled_at: campaign.scheduled_type === "now" ? null : campaign.scheduled_at ?? null,
  };
}

export function emailCampaignSchedulePath(campaignId: number): string {
  return `/admin/email-marketing/campaigns/${campaignId}/schedule`;
}

export function emailCampaignCreateSchedulePath(campaignId: number): string {
  return `/admin/email-marketing/campaigns/${campaignId}/new?step=4`;
}

export function resolveEmailCampaignSchedulePath(
  campaignId: number,
  source: "create" | "edit",
): string {
  if (source === "create") {
    return emailCampaignCreateSchedulePath(campaignId);
  }

  return emailCampaignSchedulePath(campaignId);
}

export function resolveEmailCampaignSchedulePathFromReturnTo(
  campaignId: number,
  locationState: unknown,
): string {
  if (typeof locationState === "object" && locationState !== null) {
    const returnTo = (locationState as { returnTo?: unknown }).returnTo;
    if (typeof returnTo === "string" && returnTo.includes("/new")) {
      return emailCampaignCreateSchedulePath(campaignId);
    }
  }

  return emailCampaignSchedulePath(campaignId);
}
